/* ==========================================================================
   ELVA — cart drawer extras: upsell ADD buttons, package-protection toggle.

   Uses the same CartLinesUpdateEvent flow as elva-cart-drawer-fix.js so
   <cart-items-component> re-renders after every mutation. Listeners are
   delegated to document because the drawer's DOM is morphed on every cart
   change — element-bound listeners would die on the first re-render.
   ========================================================================== */
import { CartLinesUpdateEvent } from '@shopify/events';

function root() {
  return window.Shopify?.routes?.root || '/';
}

function sectionIds() {
  const ids = [];
  for (const node of document.querySelectorAll('cart-items-component')) {
    const id = node.getAttribute('data-section-id');
    if (id && !ids.includes(id)) ids.push(id);
  }
  return ids;
}

function getCart() {
  return fetch(`${root()}cart.js`, {
    headers: { Accept: 'application/json' },
    cache: 'no-store'
  }).then((r) => r.json());
}

/* Perform a cart mutation and drive the theme's re-render cycle. */
function mutate(endpoint, params, { action, merchandiseId, quantity, sourceEl }) {
  const deferred = CartLinesUpdateEvent.createPromise();

  (sourceEl || document.body).dispatchEvent(
    new CartLinesUpdateEvent({
      action,
      context: 'cart-drawer',
      lines: [{ merchandiseId: String(merchandiseId), quantity }],
      promise: deferred.promise
    })
  );

  const body = new URLSearchParams(params);
  const ids = sectionIds();
  if (ids.length) body.set('sections', ids.join(','));

  return fetch(root() + endpoint, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/x-www-form-urlencoded'
    },
    body: body.toString()
  })
    .then((response) => response.json().then((data) => ({ ok: response.ok, data: data || {} })))
    .then((result) =>
      getCart().then((cart) => {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'elva-cart-extras',
            itemCount: quantity || 1,
            sections: result.data.sections,
            didError: !result.ok || Boolean(result.data.status)
          }
        });
        return result;
      })
    )
    .catch((error) => {
      deferred.reject(error);
      throw error;
    });
}

function addVariant(variantId, sourceEl) {
  return mutate(
    'cart/add.js',
    { id: String(variantId), quantity: '1' },
    { action: 'add', merchandiseId: variantId, quantity: 1, sourceEl }
  );
}

function setVariantQty(variantId, qty, sourceEl) {
  return mutate(
    'cart/change.js',
    { id: String(variantId), quantity: String(qty) },
    { action: 'update', merchandiseId: variantId, quantity: qty, sourceEl }
  );
}

/* ------------------------------------------------------------- upsells */

document.addEventListener('click', (event) => {
  const btn = event.target.closest('[data-elva-upsell-add]');
  if (!btn) return;

  event.preventDefault();
  const card = btn.closest('[data-elva-upsell]');
  const select = card && card.querySelector('[data-elva-upsell-variant]');
  const variantId = (select && select.value) || btn.getAttribute('data-variant-id');
  if (!variantId) return;

  btn.disabled = true;
  addVariant(variantId, btn).finally(() => {
    btn.disabled = false;
  });
});

/* Keep the card's price in sync with the chosen variant. */
document.addEventListener('change', (event) => {
  const select = event.target.closest('[data-elva-upsell-variant]');
  if (!select) return;

  const card = select.closest('[data-elva-upsell]');
  const option = select.options[select.selectedIndex];
  if (!card || !option) return;

  const price = card.querySelector('[data-elva-upsell-price]');
  const compare = card.querySelector('[data-elva-upsell-compare]');
  if (price && option.dataset.price) price.textContent = option.dataset.price;
  if (compare) {
    compare.textContent = option.dataset.compare || '';
    compare.hidden = !option.dataset.compare;
  }
});

/* ---------------------------------------------------- package protection */

const PROTECT_OFF_KEY = 'elva-protect-off';
let protectBusy = false;

function protectToggle() {
  return document.querySelector('[data-elva-protect-toggle]');
}

document.addEventListener('change', (event) => {
  const toggle = event.target.closest('[data-elva-protect-toggle]');
  if (!toggle || protectBusy) return;

  const variantId = toggle.getAttribute('data-variant-id');
  if (!variantId) return;

  protectBusy = true;
  toggle.disabled = true;

  let run;
  if (toggle.checked) {
    try { localStorage.removeItem(PROTECT_OFF_KEY); } catch (e) { /* ignore */ }
    run = addVariant(variantId, toggle);
  } else {
    try { localStorage.setItem(PROTECT_OFF_KEY, '1'); } catch (e) { /* ignore */ }
    run = setVariantQty(variantId, 0, toggle);
  }

  run.finally(() => {
    protectBusy = false;
    const fresh = protectToggle();
    if (fresh) fresh.disabled = false;
  });
});

/* Auto-add protection once the cart has something in it, unless the shopper
   switched it off before (persisted per browser). Re-checks the live cart
   before adding so a stale DOM can never double-add. */
function maybeAutoProtect() {
  const toggle = protectToggle();
  if (!toggle || toggle.checked || protectBusy) return;
  if (toggle.getAttribute('data-auto') !== 'true') return;

  try {
    if (localStorage.getItem(PROTECT_OFF_KEY) === '1') return;
  } catch (e) { /* ignore */ }

  const variantId = Number(toggle.getAttribute('data-variant-id'));
  if (!variantId) return;

  protectBusy = true;
  getCart()
    .then((cart) => {
      const hasProtection = cart.items.some((item) => item.variant_id === variantId);
      const hasRealItems = cart.items.some((item) => item.variant_id !== variantId);
      protectBusy = false;
      if (!hasProtection && hasRealItems) {
        protectBusy = true;
        return addVariant(variantId, toggle).finally(() => {
          protectBusy = false;
        });
      }
      return null;
    })
    .catch(() => {
      protectBusy = false;
    });
}

document.addEventListener('shopify:cart:lines-update', (event) => {
  const source = event.detail && event.detail.source;
  if (source === 'elva-cart-extras') return;
  setTimeout(maybeAutoProtect, 800);
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => setTimeout(maybeAutoProtect, 500));
} else {
  setTimeout(maybeAutoProtect, 500);
}
